import type {
  ActorType,
  ApprovalResponse,
} from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc';
import {
  ApprovalStepStatus,
  ApprovalStatus,
  ApprovalRules,
} from 'ui-product-approvals-common';
import type {
  ApprovalAction,
  ApprovalConclusionReason,
  ApprovalData,
  ApprovalStep,
  ApprovalStepOperation,
  ApprovalType,
  ApprovalActor,
} from 'ui-product-approvals-common';

import { AgentTurnType } from 'ui-product-approvals-common/types/ApprovalData';

import type {
  AppHubUserRecord,
  AppTeamRecord,
  BreezeAgentRecord,
} from '../types/CrmSearchUser';
import { ACTOR } from '../constants/actors';
import { unreachableFallback } from 'ui-product-approvals-common/utils/unreachableFallback';
import unescapedText from 'I18n/utils/unescapedText';
import formatName from 'I18n/utils/formatName';

export const actorToApprovalUser = (
  actor: { actorType: ActorType; id: string },
  usersRecord: AppHubUserRecord,
  agentsRecord: BreezeAgentRecord,
  teamsRecord: AppTeamRecord = {}
): ApprovalActor => {
  const { actorType, id } = actor;

  if (typeof actorType === 'symbol') {
    return {
      actor: { actorType: ACTOR.USER, id },
      displayName: `${unescapedText(
        'productApprovals.actor.unknownName'
      )} (${id})`,
      email: '',
    };
  }

  switch (actorType) {
    case ACTOR.USER: {
      const userId = Number(id);
      const user = usersRecord[userId];
      const email = user?.email ?? '';
      const firstName = user?.firstName ?? '';
      const lastName = user?.lastName ?? '';
      return {
        actor: { actorType: ACTOR.USER, id },
        displayName:
          formatName({ firstName, lastName, email }) ||
          `${unescapedText('productApprovals.user.unknownName')} (${id})`,
        email,
      };
    }
    case ACTOR.AGENT: {
      return {
        actor: { actorType: ACTOR.AGENT, id },
        displayName: agentsRecord[id]?.displayName ?? id,
        email: '',
      };
    }
    case ACTOR.TEAM: {
      const teamName = teamsRecord[id]?.name;
      const fallbackName = unescapedText('productApprovals.team.defaultName');
      return {
        actor: { actorType: ACTOR.TEAM, id },
        displayName: teamName ?? `${fallbackName} (${id})`,
        email: '',
      };
    }
    default:
      return unreachableFallback(actorType, {
        actor: { actorType, id },
        displayName: `${unescapedText(
          'productApprovals.actor.unknownName'
        )} (${id})`,
        email: '',
      });
  }
};

const resolveActedByDisplayName = (
  actedByUserId: number | null | undefined,
  usersRecord: AppHubUserRecord
): string | undefined => {
  if (actedByUserId == null) {
    return undefined;
  }
  const user = usersRecord[actedByUserId];
  if (!user) {
    return undefined;
  }
  return (
    formatName({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
    }) || undefined
  );
};

export const toApprovalData = (
  approvalResponse: ApprovalResponse,
  usersRecord: AppHubUserRecord,
  agentsRecord: BreezeAgentRecord,
  approvalType: ApprovalType,
  approvalAction: ApprovalAction,
  teamsRecord: AppTeamRecord = {}
): NonNullable<ApprovalData> => ({
  id: approvalResponse.id,
  portalId: approvalResponse.portalId,
  title: approvalResponse.title,
  createDateTimestamp: approvalResponse.createDateTimestamp,
  approvalStatus: approvalResponse.approvalStatus as ApprovalStatus,
  approvalType,
  approvalAction,
  approvalRules: approvalResponse.approvalRules as ApprovalRules,
  requester: actorToApprovalUser(
    approvalResponse.requester,
    usersRecord,
    agentsRecord,
    teamsRecord
  ),
  approvalSteps: (approvalResponse.approvalSteps ?? []).map(
    (step): ApprovalStep => ({
      id: step.id,
      order: step.order,
      actedByDisplayName: resolveActedByDisplayName(
        step.actedByUserId,
        usersRecord
      ),
      approver: actorToApprovalUser(
        step.approver,
        usersRecord,
        agentsRecord,
        teamsRecord
      ),
      approvalStepStatus: step.approvalStepStatus as ApprovalStepStatus,
      availableOperations: (step.availableOperations ??
        []) as ApprovalStepOperation[],
      inProgressTurnType:
        step.inProgressTurnType === AgentTurnType.REVIEW ||
        step.inProgressTurnType === AgentTurnType.APPLY_CHANGES
          ? step.inProgressTurnType
          : undefined,
      conversationId: step.conversationId ?? undefined,
      reviewTurnId: step.reviewTurnId ?? undefined,
      createDateTimestamp: step.createDateTimestamp,
      approvedNote: step.approvedNote ?? '',
      changesRequestedNote: step.changesRequestedNote ?? null,
      rejectionNote: step.rejectionNote ?? null,
    })
  ),
  requestNote: approvalResponse.requestNote ?? '',
  cancelNote: approvalResponse.cancelNote ?? null,
  cancelNoteTimestamp: null,
  dueDate: approvalResponse.dueDate ?? null,
  approvalConcluded: Boolean(approvalResponse.approvalConcluded),
  approvalConclusionReason: (approvalResponse.approvalConclusionReason ??
    null) as ApprovalConclusionReason,
});
